import React from "react";
import { connect } from "react-redux";
import { Bling as AD } from "react-gpt";
import poiActions from "../../pois/actions";
import ListItem from "../../pois/components/listItem";
import { SchemaEvent, SchemaReview } from "../../pois/components/schema";
import {
  createDetailUrlFromResource,
} from "../../../../server/utils/createUrls";
import resourceTypes from "../../../../server/lib/open-planet/resourceTypes";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";
import imgix from "../../shared/imgix";
import hotelProvider from "../../shared/hotelProvider";
import { getAdData } from "../../shared/utils/createAdTargeting";
import ListItemWireframe from "./listItemWireframe";
import {
  getPartnerActivityPrice,
  getPartnerActivityDiscountedPrice,
  getPartnerActivityCurrency,
} from "../../pois/utils/partnerActivityPricing";

export default class ListItems extends React.Component {
  static _getFeatures(poi) {
    return (
      poi.providers &&
      poi.providers[0].facilities &&
      poi.providers[0].facilities.slice(0, 2)
    );
  }

  static _getImages(poi) {
    const isBookable =
      poi.type === resourceTypes.lodging ||
      poi.type === resourceTypes.partner_activity;

    if (
      poi.imageAssociations || poi.images || poi.links.image || poi.providers
    ) {
      const imgixParams = {
        w: isBookable ? 128 : 170,
        h: isBookable ? 160 : 106,
        fit: "crop",
        q: 50,
        auto: "enhance",
        crop: "entropy",
      };

      const provider = poi.type === resourceTypes.lodging
        ? hotelProvider(poi.providers)
        : "media";

      let imagePath = "";

      if (poi.type === resourceTypes.partner_activity) {
        imagePath = imgix(poi.links.image, imgixParams, poi.provider);
      } else if (poi.type === resourceTypes.lodging) {
        if (Array.isArray(poi.images) && poi.images.length) {
          imagePath = imgix(
            poi.images[0].links.pixels,
            imgixParams,
            provider.type
          );
        } else if (poi.images && typeof poi.images === "object") {
          imagePath = imgix(
            poi.images.links.pixels,
            imgixParams,
            provider.type
          );
        } else {
          imagePath = "";
        }
      } else {
        const isAssociationsArray = Array.isArray(poi.imageAssociations);
        let fromImage;

        if (isAssociationsArray) {
          fromImage = poi.imageAssociations[0].from;
        } else if (poi.imageAssociations.tag !== "food") {
          fromImage = poi.imageAssociations.from;
        }

        if (fromImage) {
          imagePath = Array.isArray(fromImage)
            ? imgix(fromImage[0].links.pixels, imgixParams, provider)
            : imgix(fromImage.links.pixels, imgixParams, provider);
        }
      }

      return {
        path: `${imagePath}`,
        orientation: isBookable ? "portrait" : "landscape",
      };
    }

    return {};
  }

  static _getPlace(poi) {
    return poi.containingPlace
      ? {
        name: poi.containingPlace.name,
        type: poi.containingPlace.placeType,
      }
      : {};
  }

  static _getArticleProps(poi) {
    if (poi.type !== resourceTypes.articles) {
      return {};
    }

    const description = poi.content[0] && poi.content[0].text;
    const imageMatch = description.match(/src="([^"]+)"/);
    let image = {};

    if (imageMatch[1]) {
      const base = "https://lonelyplanetwp.imgix.net/";
      const replaced = imageMatch[1].replace(
        "http://www.lonelyplanet.com/travel-blog/tip-article/wordpress_uploads/",
        base
      );
      const query = "?fit=min&q=40&sharp=10&vib=20&w=1470";

      image = {
        path: `${replaced}${query}`,
        orientation: "portrait",
      };
    }

    return {
      title: poi.title,
      description,
      image,
    };
  }

  static _isTopChoice(poi) {
    const attributesArray = poi.poiAttributes || poi.attributes || [];
    const topChoice = attributesArray.filter(
      attribute => attribute.name === "top_choice"
    );
    return topChoice.length > 0;
  }

  static _getCollectionItemProps(poi) {
    const title = (poi.narrativePlace) ? `Best ${poi.name} in ${poi.narrativePlace.name}` : poi.name;
    return {
      title,
      mentions: poi.mentions,
    };
  }

  static _getPlaceProps(poi) {
    if (poi.type === "place") {
      let subtype;
      if (poi.ancestry && Array.isArray(poi.ancestry)) {
        subtype = poi.ancestry[0].name;
      } else if (poi.ancestry) {
        subtype = poi.ancestry.name;
      } else {
        subtype = "Place";
      }

      return {
        description: poi.review ? poi.review.essential : "",
        slug: `https://www.lonelyplanet.com/places/${poi.id}`,
        subtype,
      };
    }
    return {};
  }

  constructor(props) {
    super(props);

    this.state = {};
    this.ads = {};

    ["onMouseEnter", "onMouseLeave"].forEach(fn => {
      this[fn] = this[fn].bind(this);
    });
  }

  onMouseEnter(poi) {
    if (!poi.location) return;

    clearTimeout(this.leaveTimer);
    this.enterTimer = setTimeout(() => {
      this.props.dispatch(poiActions.hoverPlace(poi));
    }, 200);
  }

  onMouseLeave() {
    clearTimeout(this.enterTimer);
    this.leaveTimer = setTimeout(
      () => this.props.dispatch(poiActions.hoverPlace({})),
      200
    );
  }

  _getPartnerActivityInfo(poi, kind) {
    return poi.type === resourceTypes.partner_activity
      ? {
        duration: poi.duration,
        durationInfo: poi.durationInfo,
        difficulty: poi.difficulty.description,
        groupSize: {
          min: poi.groupSize.minimum_size || 1,
          max: poi.groupSize.maximum_size,
        },
        provider: poi.provider,
        tourStart: poi.departureInfo,
        tourEnd: poi.returnInfo,
        tourMap: poi.links.map,
        image: ListItems._getImages(poi),
        bookingUrl: poi.links.booking,
        subtype: poi.canonicalCategory,
        description: poi.descriptions,
        price: {
          amount: getPartnerActivityDiscountedPrice(poi) ||
              getPartnerActivityPrice(poi),
          currency: getPartnerActivityCurrency(poi),
        },
        activityType: kind,
        mobile: this.props.mobile,
      }
      : {};
  }

  _getSlug(poi) {
    const { params, breadcrumbs } = this.props;

    return createDetailUrlFromResource(
      poi,
      breadcrumbs.map(b => b.title),
      params.placeId
    );
  }

  _getLodgingProps(poi) {
    const { availability } = this.props;
    const price = availability
      ? {
        price: {
          amount: parseInt(poi.offerings[0].cost.amount, 10),
          currency: availability.currency,
          rate: availability.numNights > 0
              ? `${availability.numNights} night${availability.numNights !== 1 ? "s" : ""}`
              : "",
        },
      }
      : {};

    return Object.assign({}, this._getListItemProps(poi.lodging), price);
  }

  _getListItemProps(poi) {
    return {
      id: `poi-${poi.id}`,
      title: poi.name,
      slug: this._getSlug(poi),
      type: poi.resource || poi.type,
      subtype: poi.subtypes ? poi.subtypes[0] : "",
      place: ListItems._getPlace(poi),
      image: ListItems._getImages(poi),
      description: poi.review ? poi.review.essential : "",
      // TODO: Uncomment when ready for OpenTable
      // link: poi.resource === poiTypes.eating.name && {
      //   title: "Find a table",
      //   url: "/",
      // },
      address: poi.address,
      features: ListItems._getFeatures(poi),
      topChoice: ListItems._isTopChoice(poi),
      reviewed: poi.review && true,
      // TODO: Uncomment when ready for bookmarks
      // bookmark: true,
      // bookmarkSize: this.props.mobile ? "large" : "small",
      hidePrice: poi.type === resourceTypes.lodging &&
        (!this.props.availability.startDate ||
          !this.props.availability.endDate),
      onShowPrices: () => {
        if (this.props.onShowPrices) {
          this.props.onShowPrices();
        }
      },
      mobile: this.props.mobile,
    };
  }

  render() {
    const {
      pois,
      type,
      adWrapperStyle,
      mapMode,
      fetchingNext,
      mobile,
    } = this.props;

    let adsData = {};
    if (typeof window !== "undefined") {
      adsData = getAdData(window.lp.ads, location.url, window.Krux);
      adsData = Object.assign(adsData, { position: "superzone" });
    }

    const items = pois.map((poi, i) => {
      const ListItemType = type.component || ListItem;

      const partnerActivityProps = this._getPartnerActivityInfo(poi, type.kind);
      const collectionProps = poi.narrativePlace ? ListItems._getCollectionItemProps(poi) : {};
      const listItemProps = poi.type === resourceTypes.availability
        ? this._getLodgingProps(poi)
        : this._getListItemProps(poi);

      const articleProps = ListItems._getArticleProps(poi);
      const placeProps = ListItems._getPlaceProps(poi);

      const showAd = i % 10 === 2;

      return (
        <div
          className="ListItem-container"
          key={`${poi.id}-${poi.type}`}
          onMouseEnter={() => {
            if (mapMode === "hover") {
              this.onMouseEnter(poi);
            }
          }}
          onMouseLeave={() => {
            if (mapMode === "hover") {
              this.onMouseLeave();
            }
          }}
        >
          {(poi.type === resourceTypes.poi &&
            poi.poiType.toLowerCase() === poiTypes.festivals_events.name) &&
            <SchemaEvent poi={poi} />
          }

          {((poi.type === resourceTypes.poi || poi.type === resourceTypes.lodging) &&
            poi.poiType.toLowerCase() !== poiTypes.festivals_events.name) &&
            <SchemaReview poi={poi} />
          }

          <ListItemType
            {...listItemProps}
            {...partnerActivityProps}
            {...articleProps}
            {...collectionProps}
            {...placeProps}
          />

          {showAd &&
            <div className="Ad" style={adWrapperStyle} ref={node => { this.ads[`ad-${poi.id}`] = node; }}>
              <AD
                sizeMapping={[
                  { viewport: [0, 0], slot: [1, 1] },
                  { viewport: [969, 0], slot: [970, 250] },
                ]}
                adUnitPath="/9885583/LonelyPlanet.com/Destinations/Lists"
                targeting={adsData}
              />
            </div>}
        </div>
      );
    });

    return (
      <div className="ListItems-container">
        {items}
        {fetchingNext &&
          <ListItemWireframe type={mobile ? "mobile" : "desktop"} />}
      </div>
    );
  }
}

ListItems.propTypes = {
  pois: React.PropTypes.array,
  type: React.PropTypes.string,
  params: React.PropTypes.object,
  adWrapperStyle: React.PropTypes.object,
  breadcrumbs: React.PropTypes.array,
  mapMode: React.PropTypes.string,
  fetchingNext: React.PropTypes.bool,
  mobile: React.PropTypes.bool,
  onShowPrices: React.PropTypes.func,
  dispatch: React.PropTypes.func,
  availability: React.PropTypes.object,
};

const mapStateToProps = state => ({
  availability: state.availability,
});

const connected = connect(mapStateToProps)(ListItems);
export { connected };



// WEBPACK FOOTER //
// ./assets/list/components/listItems.jsx